namespace Serenity {
    @Decorators.registerInterface('Serenity.IAsyncInit')
    export class IAsyncInit {
    }

    export interface WidgetClass<TOptions = object> {
        element: JQuery;

        new(element: JQuery, options?: TOptions): Widget<TOptions>;
    }

    export interface WidgetDialogClass<TOptions = object> {
        element: JQuery;

        new(options?: TOptions): Widget<TOptions> & IDialog;
    }

    export type AnyWidgetClass<TOptions = object> = WidgetClass<TOptions> | WidgetDialogClass<TOptions>;

    if (typeof React === "undefined") {
        if (window["preact"] != null) {
            window["React"] = window["ReactDOM"] = window["preact"];
            (React as any).Fragment = Q.coalesce((React as any).Fragment, "x-fragment");
        } else if (window["Nerv"] != null) {
            window["React"] = window['ReactDOM'] = window["Nerv"];
            (React as any).Fragment = Q.coalesce((React as any).Fragment, "x-fragment");
        } else {
            window["React"] = {
                Component: function () {
                } as any,
                Fragment: "x-fragment" as any,
                createElement: function () {
                    return {_reactNotLoaded: true};
                } as any
            } as any;
            window["ReactDOM"] = {
                render: function () {
                    throw Error("To use React, it should be included before Serenity.CoreLib.js");
                }
            };
        }
    }

    @Serenity.Decorators.registerClass()
    export class Widget<TOptions> extends React.Component<TOptions, any> {
        private static nextWidgetNumber = 0;
        private static __isWidgetType = true;
        public element: JQuery;
        props: Readonly<{ children?: React.ReactNode }> & Readonly<TOptions> & WidgetComponentProps<this>;
        protected options: TOptions;
        protected widgetName: string;
        protected uniqueName: string;
        protected asyncPromise: PromiseLike<void>;

        constructor(element: JQuery, options?: TOptions) {
            super(options);

            this.element = element;
            this.options = options || ({} as TOptions);

            this.widgetName = Widget.getWidgetName((ss as any).getInstanceType(this));
            this.uniqueName = this.widgetName + (Widget.nextWidgetNumber++).toString();

            if (element.data(this.widgetName)) {
                throw new ss.Exception(Q.format("The element already has widget '{0}'!", this.widgetName));
            }

            element.bind(`remove.${this.widgetName}`, e => {
                if (e.bubbles || e.cancelable) {
                    return;
                }

                this.destroy();
            }).data(this.widgetName, this);

            this.addCssClass();

            if (this.isAsyncWidget()) {
                window.setTimeout(() => {
                    if (element && !this.asyncPromise) {
                        this.asyncPromise = this.initializeAsync();
                    }
                });
            }
        }

        public static getWidgetName(type: Function): string {
            return Q.replaceAll((ss as any).getTypeFullName(type), ".", "_");
        }

        public static elementFor<TWidget>(editorType: { new(...args: any[]): TWidget }): JQuery {
            var elementAttr = (ss as any).getAttributes(editorType, Serenity.ElementAttribute, true);
            var elementHtml = ((elementAttr.length) ? elementAttr[0].value : "<input />");

            return $(elementHtml);
        };

        public static create<TWidget extends Widget<TOpt>, TOpt>(params: CreateWidgetParams<TWidget, TOpt>) {
            let widget: TWidget;

            if ((ss as any).isAssignableFrom(Serenity.IDialog, params.type)) {
                widget = new (params.type as any)(params.options);

                if (params.container) {
                    widget.element.appendTo(params.container);
                }

                params.element && params.element(widget.element);
            } else {
                var e = Widget.elementFor(params.type);

                if (params.container) {
                    e.appendTo(params.container);
                }

                params.element && params.element(e);
                widget = new params.type(e, params.options);
            }

            if (widget.isAsyncWidget()) {
                widget.init(params.init);
            } else {
                widget.init(null);
                params.init && params.init(widget);
            }

            return widget;
        }

        public destroy(): void {
            this.element.removeClass(`s-${(ss as any).getTypeName((ss as any).getInstanceType(this))}`);
            this.element.unbind(`.${this.widgetName}`).unbind(`.${this.uniqueName}`).removeData(this.widgetName);
            this.element = null;
            this.asyncPromise = null;
        }

        public init(action?: (widget: any) => void): this {
            var promise = this.initialize();

            if (action) {
                promise.then(() => action(this));
            }

            return this;
        }

        public initialize(): PromiseLike<void> {
            if (!this.isAsyncWidget()) {
                return Promise.resolve();
            }

            if (!this.asyncPromise) {
                this.asyncPromise = this.initializeAsync();
            }

            return this.asyncPromise;
        }

        protected addCssClass(): void {
            this.element.addClass(this.getCssClass());
        }

        protected getCssClass(): string {
            /* TODO: replace Multiple file upload class */
            var type = (ss as any).getInstanceType(this);
            var klass = `s-${(ss as any).getTypeName(type)}`;
            var fullClass = Q.replaceAll((ss as any).getTypeFullName(type), ".", "-");

            for (let k of Q.Config.rootNamespaces) {
                if (Q.startsWith(fullClass, k + '-')) {
                    fullClass = fullClass.substr(k.length + 1);

                    break;
                }
            }

            fullClass = `s-${fullClass}`;

            if (klass === fullClass) {
                return klass;
            }

            return `${klass} ${fullClass}`;
        }

        protected initializeAsync(): PromiseLike<void> {
            return Promise.resolve();
        }

        protected isAsyncWidget(): boolean {
            return (ss as any).isInstanceOfType(this, Serenity.IAsyncInit);
        }

        protected show(): void {
            this.element.show();
        }

        protected hide(): void {
            this.element.hide();
        }
    }

    export interface WidgetComponentProps<W extends Serenity.Widget<any>> {
        id?: string;
        name?: string;
        className?: string;
        maxLength?: number;
        placeholder?: string;
        setOptions?: any;
        required?: boolean;
        readOnly?: boolean;
        oneWay?: boolean;
        onChange?: (event: JQueryEventObject) => void;
        onChangeSelect2?: (event: JQueryEventObject) => void;
        value?: any;
        defaultValue?: any;
    }

    export declare interface Widget<TOptions> {
        addValidationRule(eventClass: string, rule: (p1: JQuery) => string): JQuery;

        getGridField(): JQuery;

        change(handler: (event: JQueryEventObject) => void): void;

        changeSelect2(handler: (event: JQueryEventObject) => void): void;
    }

    Widget.prototype.addValidationRule = function (eventClass: string, rule: (p1: JQuery) => string): JQuery {
        return VX.addValidationRule(this.element, eventClass, rule);
    }
}